/**
 * COMPONENT_SPEC §6 — the back office. OWNER: web-frontend-engineer.
 *
 * Same tokens, same type, same palette, same voice as the client portal.
 * DENSITY IS THE ONLY PERMITTED DIFFERENCE — there is no second design language
 * here, and the people who make the suits get the same care as the people who
 * buy them.
 */

import { useState } from 'react';
import type { OrderState, OrderTransitionEvent } from '@pt/core';
import type { ApiRejection } from '@pt/api-client';
import { t, tDynamic, type CopyKey } from '@pt/copy';
import type { AtelierOrderPayload, LedgerRow } from '../lib/contracts.ts';
import { dateInWords, joinList, metres, metresSigned, timeOfDay } from '@pt/view';
import { refusalCopy } from '../lib/refusal.ts';
import {
  Button,
  ErrorStatement,
  FactLine,
  RovingRadioGroup,
  Statement,
  TextInput,
} from './primitives.tsx';

/* ---------------------------------------------------------- BackOfficeTable */

export interface Column<Row> {
  readonly id: string;
  readonly headingKey: CopyKey;
  readonly numeric?: boolean;
  readonly render: (row: Row) => React.ReactNode;
}

export interface BackOfficeTableProps<Row> {
  readonly caption: string;
  readonly columns: readonly Column<Row>[];
  readonly rows: readonly Row[];
  readonly rowKey: (row: Row) => string;
  readonly rowHref?: (row: Row) => string;
  readonly onOpenRow?: (row: Row) => void;
}

/**
 * ONE table component for every back-office list. The production queue is this
 * table FILTERED, never a second table component.
 *
 * `KEY-TABLE`: a real table with `th scope`, THE WHOLE ROW IS ONE LINK with no
 * competing links inside it, arrow keys do not hijack it — it is a list of
 * links, not a grid widget. No inline state changes and no bulk actions: a
 * transition asserts that a person did a thing, and a "mark 12 cut" button
 * turns the audit trail into fiction. No zebra striping.
 */
export function BackOfficeTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  rowHref,
  onOpenRow,
}: BackOfficeTableProps<Row>) {
  return (
    <table className="pt-table">
      <caption className="pt-visually-hidden">{caption}</caption>
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.id}
              scope="col"
              className={column.numeric === true ? 'pt-table__num' : undefined}
            >
              {t(column.headingKey)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={rowKey(row)}
            {...(rowHref === undefined
              ? {}
              : {
                  tabIndex: 0,
                  role: 'link',
                  onClick: () => onOpenRow?.(row),
                  onKeyDown: (event: React.KeyboardEvent) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onOpenRow?.(row);
                    }
                  },
                })}
          >
            {columns.map((column) => (
              <td
                key={column.id}
                className={column.numeric === true ? 'pt-table__num' : undefined}
              >
                {column.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* -------------------------------------------------------------- SearchField */

/**
 * The one search construction for a back-office list — a visible label above
 * a text field, exactly as the Clients screen has built it since ROAD-1. The
 * cloth list took it up on 5 September 2026 (finding C3), and it lives here so
 * the two cannot drift. Never a placeholder as a label; never an icon alone.
 */
export function SearchField({
  id,
  label,
  value,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}) {
  return (
    <>
      <label className="pt-input__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="pt-input__field"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </>
  );
}

/* ---------------------------------------------------------- StockLedgerRow */

/**
 * One row of the append-only movement ledger, NEWEST LAST so the running
 * balance reads down the page. The negative delta carries the minus sign and
 * nothing else — no red, no green, no arrow glyph.
 */
export function StockLedgerRow({ movement }: { movement: LedgerRow }) {
  return (
    <tr>
      <td>{dateInWords(movement.at)}</td>
      <td>{movement.actorName}</td>
      <td>{tDynamic(`move.reason.${movement.reason}`)}</td>
      <td className="pt-table__num">{metresSigned(movement.deltaM)}</td>
      <td className="pt-table__num">{metres(movement.runningBalanceM)}</td>
      <td className="pt-identifier">{movement.orderRef ?? ''}</td>
    </tr>
  );
}

/**
 * The movements ledger with its own column words. Until 5 September 2026
 * (finding C10) the headers were borrowed: `move.delta.label` — the FORM's
 * whole input label, "How much moved — positive in, negative out (m)" — stood
 * over a column of signed figures, and the order queue's "Placed" and "Client"
 * stood over a date and a column of STAFF. A header is a column's name, not
 * the sentence that asked for the value.
 */
export function StockLedger({ ledger }: { ledger: readonly LedgerRow[] }) {
  return (
    <table className="pt-table">
      <caption className="pt-visually-hidden">{t('clothdet.ledger.heading')}</caption>
      <thead>
        <tr>
          <th scope="col">{t('clothdet.ledger.column.date')}</th>
          <th scope="col">{t('clothdet.ledger.column.actor')}</th>
          <th scope="col">{t('clothdet.ledger.column.reason')}</th>
          <th scope="col" className="pt-table__num">
            {t('clothdet.ledger.column.delta')}
          </th>
          <th scope="col" className="pt-table__num">
            {t('clothlist.column.onHand')}
          </th>
          <th scope="col">{t('queue.column.order')}</th>
        </tr>
      </thead>
      <tbody>
        {ledger.map((movement) => (
          <StockLedgerRow key={movement.movementId} movement={movement} />
        ))}
      </tbody>
    </table>
  );
}

/* --------------------------------------------------------- TransitionControl */

export interface TransitionControlProps {
  /** THE control set, taken from the server. The front end holds no copy of the transition table. */
  readonly allowedEvents: readonly OrderTransitionEvent[];
  readonly onChoose: (event: OrderTransitionEvent) => void;
  readonly terminal: boolean;
  /** `ST-ORDDET-NOTMINE` — a statement naming the role that owns it, never a disabled control. */
  readonly notMineStatement?: string | null;
  readonly refusal?: React.ReactNode;
}

export function TransitionControl({
  allowedEvents,
  onChoose,
  terminal,
  notMineStatement,
  refusal,
}: TransitionControlProps) {
  /* 5 September 2026 (finding C6). Two acts leave the line rather than move
     along it: HALT, and CANCEL. Cancel used to count as "forward", so wherever
     it was the only other act — the admin at `placed`, anyone at `halted` — it
     was the ONE forward act and therefore the one FILLED button on the page,
     with Resume standing quiet beneath a rule. The filled button is the act
     that moves the suit; the act that ends it is quiet, beside halt. Resume
     puts a halted order back on the line, so it is a forward act. */
  const forward = allowedEvents.filter((e) => e !== 'order.halt' && e !== 'order.cancel');
  const halt = allowedEvents.includes('order.halt');
  const cancel = allowedEvents.includes('order.cancel');

  if (terminal) {
    return (
      <div>
        {refusal}
        <Statement>{t('orddet.terminal.statement')}</Statement>
      </div>
    );
  }

  return (
    <div>
      {refusal}
      {forward.length === 0 && notMineStatement != null ? (
        <Statement>{notMineStatement}</Statement>
      ) : null}
      <div className="pt-actions pt-actions--equal">
        {forward.map((event) => (
          <Button
            key={event}
            // ST-ORDDET-TWOFORWARD: at `fitting` both are the SAME variant, at
            // equal weight. A large primary and a small grey alternative is an
            // asymmetric choice and is excluded.
            variant={forward.length === 1 ? 'primary' : 'secondary'}
            onClick={() => onChoose(event)}
          >
            {tDynamic(`event.${event}.label`)}
          </Button>
        ))}
      </div>
      {forward.map((event) => {
        const consequence = consequenceOf(event);
        // Resume states its consequence inside its own confirm step, where the
        // stage is known; an empty line is not printed in its place.
        return consequence === '' ? null : (
          <FactLine key={`${event}-consequence`}>{consequence}</FactLine>
        );
      })}
      {/* Halt is available on every non-terminal state, and cancel where the
          machine allows it: visually separated, plainly labelled, quiet, and
          NEVER dressed as a scary destructive action — there is no destructive
          variant in this product to dress either with. */}
      {halt || cancel ? (
        <>
          <hr className="pt-rule" />
          <div className="pt-actions">
            {halt ? (
              <Button onClick={() => onChoose('order.halt')}>{t('event.order.halt.label')}</Button>
            ) : null}
            {cancel ? (
              <Button onClick={() => onChoose('order.cancel')}>
                {t('event.order.cancel.label')}
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

function consequenceOf(event: OrderTransitionEvent): string {
  // `order.halt`/`order.resume` consequences carry a {stageLabel} slot and are
  // stated inside their own confirm step, where the stage is known.
  if (event === 'order.halt' || event === 'order.resume') return '';
  return tDynamic(`event.${event}.consequence`);
}

/* ---------------------------------------------------------------- ConfirmStep */

export interface ConfirmStepProps {
  readonly heading: string;
  readonly consequence: string;
  readonly irreversible?: string | null;
  readonly children?: React.ReactNode;
  readonly proceedLabel: string;
  readonly busyLabel: string;
  readonly busy: boolean;
  readonly onProceed: () => void;
  readonly onStepBack: () => void;
  readonly refusal?: React.ReactNode;
}

/**
 * A statement of consequence, not "Are you sure?" theatre. A REGION IN THE
 * FLOW, not a modal: no overlay, no backdrop, no dialog chrome, no card, no
 * focus trap, and `Escape` is not a dismissal contract because there is nothing
 * to dismiss. The two actions are the SAME variant, equally legible.
 */
export function ConfirmStep({
  heading,
  consequence,
  irreversible,
  children,
  proceedLabel,
  busyLabel,
  busy,
  onProceed,
  onStepBack,
  refusal,
}: ConfirmStepProps) {
  return (
    <section className="pt-confirmstep" role="group" aria-label={heading}>
      <h3 className="pt-cutsheet__blockTitle">{heading}</h3>
      <Statement>{consequence}</Statement>
      {irreversible == null ? null : <FactLine>{irreversible}</FactLine>}
      {refusal}
      {children}
      <div className="pt-actions pt-actions--equal">
        <Button onClick={onProceed} busy={busy} busyLabel={busyLabel}>
          {proceedLabel}
        </Button>
        <Button onClick={onStepBack}>{t('global.action.back')}</Button>
      </div>
    </section>
  );
}

/* ------------------------------------------------------- PromisedWindowField */

export interface PromisedWindowFieldProps {
  readonly earliest: string;
  readonly latest: string;
  readonly onChange: (next: { earliest: string; latest: string }) => void;
  /** The house lead-time window, offered as a one-action fill. NEVER a pre-fill. */
  readonly houseWindow: { readonly earliest: string; readonly latest: string } | null;
}

/**
 * BOTH FIELDS ARE EMPTY ON LOAD. There is no pre-fill, on any screen, ever.
 *
 * A pre-filled window a tailor never examined is a system-invented date wearing
 * a human's signature (D-026(d), FLOWS_SPEC §13.3). The house lead time is
 * offered as a labelled action the tailor must actively press, and nothing
 * derived from `pricing.leadTimeDays` reaches a client screen — this control is
 * the only place that number is even offered, and only to a person who can
 * judge it.
 */
export function PromisedWindowField({
  earliest,
  latest,
  onChange,
  houseWindow,
}: PromisedWindowFieldProps) {
  return (
    <fieldset className="pt-filter">
      <legend className="pt-filter__legend">{t('confirm.window.label')}</legend>
      <TextInput
        label={t('confirm.window.earliest')}
        type="date"
        value={earliest}
        onChange={(value) => onChange({ earliest: value, latest })}
      />
      <TextInput
        label={t('confirm.window.latest')}
        type="date"
        value={latest}
        onChange={(value) => onChange({ earliest, latest: value })}
      />
      <FactLine>{t('confirm.window.help')}</FactLine>
      {houseWindow === null ? null : (
        <div className="pt-actions">
          <Button onClick={() => onChange({ ...houseWindow })}>{t('confirm.window.fill')}</Button>
        </div>
      )}
    </fieldset>
  );
}

/* ------------------------------------------------------- StockMovementForm */

export interface StockMovementFormProps {
  readonly clothName: string;
  readonly currentOnHandM: number;
  readonly busy: boolean;
  readonly onSubmit: (move: { deltaM: number; reason: string; note: string }) => void;
  readonly refusal?: React.ReactNode;
}

const MOVE_REASONS = [
  'mill_delivery',
  'cut',
  'stocktake_correction',
  'damage',
  'returned_to_mill',
  'sample_cut',
] as const;

/**
 * STOCK IS NEVER SET TO A NUMBER. IT IS ONLY EVER MOVED.
 *
 * There is no field in this UI that accepts a target on-hand quantity —
 * including for a stocktake, where the correction is entered as the difference.
 * An absolute set destroys the answer to "why", which is the only question
 * stock data ever gets asked.
 */
export function StockMovementForm({
  clothName,
  currentOnHandM,
  busy,
  onSubmit,
  refusal,
}: StockMovementFormProps) {
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState<string>('mill_delivery');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const parsed = Number.parseFloat(delta);
  const valid = !Number.isNaN(parsed) && parsed !== 0;
  const resulting = valid ? currentOnHandM + parsed : currentOnHandM;
  const noteRequired = reason === 'stocktake_correction' || reason === 'damage';

  return (
    <section aria-label={t('move.heading')}>
      <h2 className="pt-cutsheet__blockTitle">{t('move.heading')}</h2>
      <Statement>{t('move.statement')}</Statement>
      {refusal}
      <TextInput
        label={t('move.delta.label')}
        value={delta}
        inputMode="decimal"
        onChange={(value) => {
          setDelta(value);
          setError(null);
        }}
        {...(error === null ? {} : { error })}
      />
      {/* THIS WAS THE CONFIGURATOR'S `OptionGroup`, BORROWED, with a fake
          option called `move.reason` — so it asked the catalogue for
          `config.option.move.reason.label`, which has never existed, and
          `tDynamic` threw. Every staff role hit the error boundary on every
          cloth: the stock screen was unusable for the whole atelier.

          The fix is not seven new keys duplicating copy that is already here.
          A stock reason is not a garment option — it has no constraint, no
          reason code and nothing to grey out — so it uses the same plain
          radio group as the halt codes on the order screen, against the
          `move.reason.*` keys that were written for it. */}
      <fieldset className="pt-filter">
        <legend className="pt-filter__legend">{t('move.reason.label')}</legend>
        <RovingRadioGroup
          ariaLabel={t('move.reason.label')}
          values={MOVE_REASONS}
          chosen={reason}
          labelOf={(id) => tDynamic(`move.reason.${id}`)}
          onChoose={setReason}
        />
      </fieldset>
      <TextInput label={t('move.note.label')} value={note} onChange={setNote} multiline />
      {/* The arithmetic is not his to do. Updates in place, with no animation. */}
      <FactLine>
        {t('move.balance', {
          currentOnHand: metres(currentOnHandM),
          resultingBalance: metres(resulting),
        })}
      </FactLine>
      <div className="pt-actions">
        <Button
          variant="primary"
          busy={busy}
          busyLabel={t('trans.busy')}
          onClick={() => {
            if (!valid) return;
            if (resulting < 0) {
              // ST-MOVE-NEGATIVE — refused with the numbers stated, in place.
              setError(
                t('move.negative.statement', {
                  clothName,
                  resultingBalance: metres(resulting),
                  currentOnHand: metres(currentOnHandM),
                }),
              );
              return;
            }
            if (noteRequired && note.trim() === '') {
              setError(t('move.noteRequired', { reason: tDynamic(`move.reason.${reason}`) }));
              return;
            }
            onSubmit({ deltaM: parsed, reason, note });
          }}
        >
          {t('move.action')}
        </Button>
      </div>
    </section>
  );
}

/**
 * `ST-ORDDET-ILLEGAL` / `-CONFLICT` / `-FORBIDDEN`. Rendered IN PLACE above the
 * control, never as a toast, never a generic error, never a machine code alone
 * and never a silent no-op. The legal set comes from the REFUSAL'S OWN BODY.
 */
export function TransitionRefusal({
  rejection,
  data,
}: {
  rejection: ApiRejection;
  data: AtelierOrderPayload;
}) {
  const body = rejection.body as unknown as {
    code: string;
    message: string;
    detail?: {
      allowedEvents?: readonly OrderTransitionEvent[];
      actorName?: string;
      at?: string;
      state?: OrderState;
      roleLabel?: string;
      eventLabel?: string;
    };
  };
  const detail = body.detail ?? {};

  if (body.code === 'ILLEGAL_TRANSITION') {
    const legal = detail.allowedEvents ?? data.allowedEvents;
    return (
      <ErrorStatement
        what={body.message}
        means={t('orddet.illegal.legal', {
          stageLabel: tDynamic(`atelier.stage.${detail.state ?? data.state}`),
          roleLabel: detail.roleLabel ?? '',
          allowedEventList: joinList(legal.map((event) => tDynamic(`event.${event}.label`))),
        })}
        todo={t('orddet.illegal.do')}
        assertive
      />
    );
  }
  if (body.code === 'TRANSITION_CONFLICT') {
    return (
      <ErrorStatement
        what={t('orddet.conflict.statement', {
          actorName: detail.actorName ?? '',
          stageLabel: tDynamic(`atelier.stage.${detail.state ?? data.state}`),
          eventTime: detail.at === undefined ? '' : timeOfDay(detail.at),
        })}
        means={t('orddet.conflict.reloaded')}
        assertive
      />
    );
  }
  if (body.code === 'TRANSITION_FORBIDDEN_FOR_ROLE') {
    return (
      <ErrorStatement
        what={t('orddet.forbidden.statement', {
          eventLabel: detail.eventLabel ?? '',
          roleLabel: detail.roleLabel ?? '',
        })}
        assertive
      />
    );
  }
  return <ErrorStatement what={refusalCopy(rejection)} means={t('trans.refused.statement')} assertive />;
}


/* ------------------------------------------------------------- refusal copy */

/**
 * Render a server refusal's HUMAN COPY. The machine code is logged and never
 * shown ([T-73]). Where the catalogue carries a key for the code we resolve it
 * with the server's own slots; otherwise the server's message stands.
 */
export function RefusalStatement({
  rejection,
  assertive = false,
}: {
  rejection: ApiRejection;
  assertive?: boolean;
}) {
  return <ErrorStatement what={refusalCopy(rejection)} assertive={assertive} />;
}
